import React, { memo, useCallback, useMemo, useState } from 'react';
import { BubbleMenu } from '@tiptap/react/menus';
import { useEditorState } from '@tiptap/react';
import { Box, Button, ButtonBase, InputBase, Paper, Tooltip } from '@mui/material';
import {
  AutoAwesome,
  Code,
  FormatBold,
  FormatItalic,
  FormatStrikethrough,
  FormatUnderlined,
  Link as LinkIcon,
  LinkOff,
} from '@mui/icons-material';

/**
 * Find the generated-document field (block wrapper or inline mark) at the selection.
 * @param {import('@tiptap/core').Editor} editor - Editor.
 * @returns {{fieldId: string, pos: number|null, status: string|null}|null} Field info.
 */
export const getFieldAtSelection = (editor) => {
  if (!editor) return null;
  const { $from } = editor.state.selection;
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    const node = $from.node(depth);
    if (node.type.name === 'generatedField' && node.attrs.fieldId) {
      return { fieldId: node.attrs.fieldId, pos: $from.before(depth), status: node.attrs.status || null };
    }
  }
  const mark = $from.marks().find((m) => m.type.name === 'generatedFieldMark' && m.attrs.fieldId);
  return mark ? { fieldId: mark.attrs.fieldId, pos: null, status: mark.attrs.status || null } : null;
};

const BubbleButton = ({ label, active, onClick, children }) => (
  <Tooltip title={label} disableInteractive>
    <ButtonBase
      aria-label={label}
      aria-pressed={active || undefined}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      sx={{
        width: 30,
        height: 30,
        borderRadius: '7px',
        color: active ? 'var(--q-tooltip-text)' : 'color-mix(in srgb, var(--q-tooltip-text) 86%, transparent)',
        bgcolor: active ? 'color-mix(in srgb, var(--q-tooltip-text) 18%, transparent)' : 'transparent',
        '&:hover': { bgcolor: 'color-mix(in srgb, var(--q-tooltip-text) 14%, transparent)' },
        '& svg': { fontSize: 18 },
      }}
    >
      {children}
    </ButtonBase>
  </Tooltip>
);

const menuPaperSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  p: 0.5,
  borderRadius: '10px',
  bgcolor: 'var(--q-tooltip-bg)',
  color: 'var(--q-tooltip-text)',
  boxShadow: 'var(--q-shadow-xl)',
};

/**
 * Selection bubble menu: quick marks plus "Ask AI". Kept mounted for the editor's lifetime
 * (plugins are not re-registered), with a stable shouldShow that reads review state from
 * runtime storage.
 * @param {{editor: import('@tiptap/core').Editor, onAskAi: () => void}} props - Props.
 * @returns {JSX.Element}
 */
const SelectionBubbleMenu = memo(({ editor, onAskAi }) => {
  const [linkDraft, setLinkDraft] = useState(null);
  const state = useEditorState({
    editor,
    selector: ({ editor: ed }) => (ed ? {
      bold: ed.isActive('bold'),
      italic: ed.isActive('italic'),
      underline: ed.isActive('underline'),
      strike: ed.isActive('strike'),
      code: ed.isActive('code'),
      link: ed.isActive('link'),
    } : null),
  });

  const shouldShow = useCallback(({ editor: ed, state: pmState, from, to }) => {
    if (!ed || !ed.isEditable || from === to) return false;
    if (ed.storage.quillRuntime?.reviewActive) return false;
    const { selection } = pmState;
    if (selection.node) return false;
    if (pmState.doc.textBetween(from, to, ' ').trim() === '') return false;
    return true;
  }, []);
  const options = useMemo(() => ({ placement: 'top', offset: 8 }), []);

  return (
    <BubbleMenu editor={editor} pluginKey="quillSelectionMenu" shouldShow={shouldShow} options={options} updateDelay={120}>
      {linkDraft !== null ? (
        <Paper
          elevation={0}
          sx={{ ...menuPaperSx, px: 1 }}
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            const href = linkDraft.trim();
            const chain = editor.chain().focus().extendMarkRange('link');
            if (!href) chain.unsetLink().run();
            else chain.setLink({ href: /^(https?:|mailto:|tel:|#|\/)/i.test(href) ? href : `https://${href}` }).run();
            setLinkDraft(null);
          }}
        >
          <InputBase
            autoFocus
            value={linkDraft}
            onChange={(e) => setLinkDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                setLinkDraft(null);
                editor.commands.focus();
              }
            }}
            onBlur={() => setLinkDraft(null)}
            placeholder="Paste a link and press Enter"
            inputProps={{ 'aria-label': 'Link URL' }}
            sx={{ color: 'var(--q-tooltip-text)', fontSize: 13, width: 240, px: 0.5 }}
          />
          {state?.link && (
            <BubbleButton
              label="Remove link"
              onClick={() => {
                editor.chain().focus().extendMarkRange('link').unsetLink().run();
                setLinkDraft(null);
              }}
            >
              <LinkOff />
            </BubbleButton>
          )}
        </Paper>
      ) : (
      <Paper elevation={0} sx={menuPaperSx} role="toolbar" aria-label="Selection formatting">
        <BubbleButton label="Bold" active={state?.bold} onClick={() => editor.chain().focus().toggleBold().run()}><FormatBold /></BubbleButton>
        <BubbleButton label="Italic" active={state?.italic} onClick={() => editor.chain().focus().toggleItalic().run()}><FormatItalic /></BubbleButton>
        <BubbleButton label="Underline" active={state?.underline} onClick={() => editor.chain().focus().toggleUnderline().run()}><FormatUnderlined /></BubbleButton>
        <BubbleButton label="Strikethrough" active={state?.strike} onClick={() => editor.chain().focus().toggleStrike().run()}><FormatStrikethrough /></BubbleButton>
        <BubbleButton label="Inline code" active={state?.code} onClick={() => editor.chain().focus().toggleCode().run()}><Code /></BubbleButton>
        <BubbleButton
          label="Link"
          active={state?.link}
          onClick={() => setLinkDraft(editor.getAttributes('link')?.href || '')}
        >
          <LinkIcon />
        </BubbleButton>
        <Box aria-hidden sx={{ width: '1px', height: 18, bgcolor: 'color-mix(in srgb, var(--q-tooltip-text) 24%, transparent)', mx: 0.5 }} />
        <Button
          size="small"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onAskAi}
          startIcon={<AutoAwesome sx={{ fontSize: '16px !important' }} />}
          sx={{ color: 'var(--q-tooltip-text)', height: 30, px: 1.25, borderRadius: '7px', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: 'color-mix(in srgb, var(--q-tooltip-text) 14%, transparent)' } }}
        >
          Ask AI
        </Button>
      </Paper>
      )}
    </BubbleMenu>
  );
});
SelectionBubbleMenu.displayName = 'SelectionBubbleMenu';


export { SelectionBubbleMenu };
